import React from 'react';
import { AnalysisResponse } from '../services/imageService';

export interface AppContextType {
  imageAnalysis: AnalysisResponse | null;
  currentQuestionIndex: number;
  setImageAnalysis: (analysis: AnalysisResponse | null) => void;
  setCurrentQuestionIndex: (index: number) => void;
  formattedPosts: string[];
  setFormattedPosts: (posts: string[]) => void;
  onImageSubmit: (analysisJson: string) => void;
  onQuestionSelect: (index: number) => void;
  onRecordingComplete: (audioBlob: Blob, generatedPost: string) => void;
}

export const AppContext = React.createContext<AppContextType | null>(null);

interface AppProviderProps {
  children: React.ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [imageAnalysis, setImageAnalysis] = React.useState<AnalysisResponse | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
  const [formattedPosts, setFormattedPosts] = React.useState<string[]>([]);

  const handleImageSubmit = React.useCallback((analysisJson: string) => {
    try {
      const analysis = JSON.parse(analysisJson) as AnalysisResponse;
      setImageAnalysis(analysis);
      setCurrentQuestionIndex(0); // Reset to first question
    } catch (error) {
      console.error('Error parsing image analysis:', error);
    }
  }, []);

  const handleQuestionSelect = React.useCallback((index: number) => {
    if (!imageAnalysis) return;
    setCurrentQuestionIndex(index);
  }, [imageAnalysis]);

  const handleRecordingComplete = React.useCallback(async (audioBlob: Blob, generatedPost: string) => {
    setFormattedPosts(prev => [...prev, generatedPost]);
    
    if (imageAnalysis && currentQuestionIndex < imageAnalysis.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [imageAnalysis, currentQuestionIndex]);

  const value = React.useMemo(() => ({
    imageAnalysis,
    currentQuestionIndex,
    setImageAnalysis,
    setCurrentQuestionIndex,
    formattedPosts,
    setFormattedPosts,
    onImageSubmit: handleImageSubmit,
    onQuestionSelect: handleQuestionSelect,
    onRecordingComplete: handleRecordingComplete,
  }), [
    imageAnalysis,
    currentQuestionIndex,
    formattedPosts,
    handleImageSubmit,
    handleQuestionSelect,
    handleRecordingComplete
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  const context = React.useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}
